import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, ExternalLink, Loader2 } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { dataQualityService } from '../../services/dataQualityService';
import { ErrorAlert } from '../shared/ErrorAlert';

const CompletenessDetail = () => {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchData = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await dataQualityService.completeness.getForClient(clientId);
            if (response.success && response.data) {
                setData(response.data);
            }
        } catch (err) {
            console.error('Error fetching completeness detail:', err);
            setError('Failed to load completeness data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [clientId]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    if (error) {
        return <ErrorAlert error={error} onRetry={fetchData} />;
    }

    if (!data) return null;

    const donutData = [
        { name: 'Completed', value: data.completedFields, color: '#10B981' },
        { name: 'Missing', value: data.missingFields, color: '#EF4444' },
    ];

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-green-600';
        if (score >= 50) return 'text-yellow-600';
        return 'text-red-600';
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/data-quality/completeness')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-900">{data.clientName}</h2>
                        <p className="text-sm text-gray-500">{data.clientCode} &middot; {data.clientType}</p>
                    </div>
                    {data.complete ? (
                        <span className="ml-auto px-3 py-1 text-sm rounded-full bg-green-100 text-green-800 font-medium">Complete</span>
                    ) : (
                        <span className="ml-auto px-3 py-1 text-sm rounded-full bg-yellow-100 text-yellow-800 font-medium">Incomplete</span>
                    )}
                </div>
            </div>

            {/* Scores + Donut */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Score Cards */}
                <div className="space-y-4">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <p className="text-sm text-gray-500 mb-1">Overall Completeness</p>
                        <p className={`text-3xl font-bold ${getScoreColor(data.completenessPercentage)}`}>
                            {Number(data.completenessPercentage).toFixed(1)}%
                        </p>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <p className="text-sm text-gray-500 mb-1">Field Score</p>
                        <p className={`text-3xl font-bold ${getScoreColor(data.fieldScore)}`}>
                            {Number(data.fieldScore).toFixed(1)}%
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                            {data.completedFields} of {data.totalRequiredFields} fields filled
                        </p>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <p className="text-sm text-gray-500 mb-1">KYC Score</p>
                        <p className={`text-3xl font-bold ${getScoreColor(data.kycScore)}`}>
                            {Number(data.kycScore).toFixed(1)}%
                        </p>
                    </div>
                </div>

                {/* Donut Chart */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Field Completion</h3>
                    <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                            <Pie
                                data={donutData}
                                cx="50%"
                                cy="50%"
                                innerRadius={70}
                                outerRadius={110}
                                paddingAngle={3}
                                dataKey="value"
                                label={({ name, value }) => `${name}: ${value}`}
                            >
                                {donutData.map((entry, idx) => (
                                    <Cell key={idx} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Field Status Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Field Breakdown</h3>
                    <button
                        onClick={() => navigate(`/clients/${data.clientId}/edit`)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                    >
                        <ExternalLink className="w-4 h-4" />
                        Edit Client
                    </button>
                </div>
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weight</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                        {(data.fieldStatuses || []).map((field) => (
                            <tr key={field.fieldName} className="hover:bg-gray-50 transition">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <p className="text-sm font-medium text-gray-900">{field.fieldLabel}</p>
                                    <p className="text-xs text-gray-400">{field.fieldName}</p>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {field.required ? 'Yes' : 'No'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {field.weight}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {field.filled ? (
                                        <span className="flex items-center gap-1 text-green-600">
                                            <CheckCircle className="w-4 h-4" />
                                            <span className="text-sm">Filled</span>
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-1 text-red-500">
                                            <XCircle className="w-4 h-4" />
                                            <span className="text-sm">Missing</span>
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CompletenessDetail;
